import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, Minus } from "lucide-react";
import AssistantPanel from "./AssistantPanel";
import useAssistantConversation, { readAssistantPreference, writeAssistantPreference } from "./useAssistantConversation";
import "../../pages/admin/backoffice.css";
import "./assistant-workspace.css";

const AssistantContext = createContext(null);
export const useAssistant = () => useContext(AssistantContext);
const PREFIX = "aimp-assistant:";

function currentScope() {
  const clinicId = localStorage.getItem("clinicId");
  const token = localStorage.getItem("token");
  if (!clinicId || !token) return null;
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const claims = JSON.parse(atob(part.padEnd(Math.ceil(part.length / 4) * 4, "=")));
    if (claims.email) return { clinicId, key: `${PREFIX}${encodeURIComponent(clinicId)}:${encodeURIComponent(claims.email)}:${claims.token_version || 1}` };
  } catch { /* Legacy sessions are kept in memory only, never under another actor's key. */ }
  return { clinicId, key: null };
}

export default function AssistantWorkspace({ children }) {
  const location = useLocation();
  const [, updateSession] = useState(0);
  useEffect(() => {
    const changed = event => {
      if (!event.key || ["token", "clinicId"].includes(event.key)) updateSession(v => v + 1);
    };
    window.addEventListener("storage", changed);
    return () => window.removeEventListener("storage", changed);
  }, []);
  const scope = currentScope();
  const active = !!scope && (/^\/dashboard(?:\/|$)/.test(location.pathname) || location.pathname === "/onboarding");
  useEffect(() => {
    if (scope) return;
    try {
      Object.keys(sessionStorage).filter(k => k.startsWith(PREFIX)).forEach(k => sessionStorage.removeItem(k));
    } catch { /* Storage can be disabled. */ }
  }, [scope]);
  return active ? (
    <ActiveWorkspace key={scope.key || scope.clinicId} clinicId={scope.clinicId} storageKey={scope.key}>
      {children}
    </ActiveWorkspace>
  ) : <AssistantContext.Provider value={null}>{children}</AssistantContext.Provider>;
}

function ActiveWorkspace({ children, clinicId, storageKey }) {
  const location = useLocation();
  const model = useAssistantConversation({ clinicId, storageKey });
  const [open, setOpen] = useState(() => readAssistantPreference(storageKey && `${storageKey}:open`) === "true"
    || location.pathname === "/onboarding" || location.pathname === "/dashboard/assistant");
  const [unread, setUnread] = useState(false);
  const launcher = useRef(null);
  const panel = useRef(null);
  const previousPath = useRef(location.pathname);
  const lastReply = useRef(model.replyVersion);
  const { refreshContext } = model;
  const openAssistant = useCallback(() => { setOpen(true); setUnread(false); refreshContext(); }, [refreshContext]);
  const minimize = () => { setOpen(false); launcher.current?.focus(); };

  useEffect(() => {
    writeAssistantPreference(storageKey && `${storageKey}:open`, String(open));
    if (open) panel.current?.querySelector("textarea")?.focus();
  }, [open, storageKey]);
  useEffect(() => {
    if (location.pathname !== previousPath.current) {
      previousPath.current = location.pathname;
      refreshContext();
    }
  }, [location.pathname, refreshContext]);
  useEffect(() => {
    const refresh = () => { if (document.visibilityState !== "hidden") refreshContext(); };
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [refreshContext]);
  useEffect(() => {
    if (model.replyVersion !== lastReply.current) {
      lastReply.current = model.replyVersion;
      if (!open) setUnread(true);
    }
  }, [model.replyVersion, open]);

  return (
    <AssistantContext.Provider value={{ ...model, openAssistant, minimize, open }}>
      <div className={`aimp-workspace${open ? " aimp-workspace--open" : ""}`}>{children}</div>
      <div className="bo-scope aimp-assistant-dock">
        <aside ref={panel} id="aimp-assistant-panel" className="aimp-assistant-panel" hidden={!open}
          aria-label="Chat de onboarding AIMP" onKeyDown={e => { if (e.key === "Escape") minimize(); }}>
          <header className="aimp-assistant-header">
            <div><strong>Asistente AIMP</strong><small>Tu configuración, paso a paso</small></div>
            <button type="button" onClick={minimize} aria-label="Minimizar asistente" title="Minimizar (Esc)"><Minus size={18} /></button>
          </header>
          <AssistantPanel clinicId={clinicId} model={model} compact />
        </aside>
        <button ref={launcher} type="button" className="aimp-assistant-launcher" onClick={open ? minimize : openAssistant}
          aria-expanded={open} aria-controls="aimp-assistant-panel">
          <MessageCircle size={20} aria-hidden="true" />
          <span>{unread ? "Nueva respuesta" : model.busy ? "Asistente respondiendo…" : "Asistente AIMP"}</span>
          {unread && <span className="aimp-unread" aria-hidden="true" />}
        </button>
      </div>
    </AssistantContext.Provider>
  );
}
